export const REMITTANCE_STEP_TYPES = ['remittance', 'remittance-key', 'remittance-balance'];
export function isRemittanceStepType(stepType) {
    return REMITTANCE_STEP_TYPES.includes(stepType);
}
/**
 * The step at which a remittance batch is FINISHED — keyed, balanced, settled.
 * The combined shape's single step, and the split shape's SECOND step.
 * `remittance-key` is deliberately excluded: keying being done does not mean the
 * money balances, so a batch draining that step has not been received in any
 * sense a ledger can post against.
 *
 * Lives here, beside REMITTANCE_STEP_TYPES, because it is a third projection of
 * the same three-value vocabulary that `validateRemittancePipelineShape` and
 * `remittancePermissionForStep` already encode — not event-specific knowledge,
 * despite its first consumer being the `capture.remittance.received` emit
 * trigger. Typed against RemittanceStepType so adding a fourth step type is a
 * compile error here rather than a silently stale literal list elsewhere.
 */
const TERMINAL_REMITTANCE_STEPS = ['remittance', 'remittance-balance'];
export function isTerminalRemittanceStep(stepType) {
    return TERMINAL_REMITTANCE_STEPS.includes(stepType);
}
/** SP3 §2 permission mapping (closes SP1 §4.4): combined + key steps need
 *  `remittance.key`; the balance step needs `remittance.balance`. Null for
 *  non-remittance steps. */
export function remittancePermissionForStep(stepType) {
    if (stepType === 'remittance' || stepType === 'remittance-key')
        return 'remittance.key';
    if (stepType === 'remittance-balance')
        return 'remittance.balance';
    return null;
}
function issue(code, message) {
    return { level: 'error', code, message };
}
/**
 * SP3 §2 — pipeline-shape validation. `stepTypes` is the pipeline's step
 * types in order. Pure; callers supply the job's surface and the job-level
 * `remittance.dualControl` flag (pipeline steps are relational rows, not V3
 * content, so this check runs api-side where both are in hand).
 */
export function validateRemittancePipelineShape(stepTypes, surface, dualControl) {
    const out = [];
    const remSteps = stepTypes.filter(isRemittanceStepType);
    if (surface !== 'remittance') {
        if (remSteps.length > 0) {
            out.push(issue('remittance_step_on_standard_surface', 'Remittance step types are only valid on remittance-surface job types.'));
        }
        return out;
    }
    for (const t of REMITTANCE_STEP_TYPES) {
        if (stepTypes.filter((s) => s === t).length > 1) {
            out.push(issue('remittance_step_duplicated', `Pipeline declares "${t}" more than once.`));
        }
    }
    const hasCombined = remSteps.includes('remittance');
    const hasKey = remSteps.includes('remittance-key');
    const hasBalance = remSteps.includes('remittance-balance');
    if (hasCombined && (hasKey || hasBalance)) {
        out.push(issue('remittance_shape_mixed', 'A pipeline uses ONE remittance shape: combined ("remittance") or split ("remittance-key" then "remittance-balance") — not both.'));
    }
    if ((hasKey || hasBalance) && !(hasKey && hasBalance)) {
        out.push(issue('remittance_split_incomplete', 'The split shape requires BOTH "remittance-key" and "remittance-balance" steps.'));
    }
    if (hasKey && hasBalance && stepTypes.indexOf('remittance-key') > stepTypes.indexOf('remittance-balance')) {
        out.push(issue('remittance_split_order', '"remittance-key" must come before "remittance-balance".'));
    }
    if (dualControl && !(hasKey && hasBalance)) {
        out.push(issue('remittance_dual_control_requires_split', 'remittance.dualControl requires the split pipeline shape (remittance-key → remittance-balance).'));
    }
    return out;
}
//# sourceMappingURL=step-types.js.map